import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { toast } from "../../util/Toast";

// ==========================================
// CONFIGURATION & ENDPOINTS
// ==========================================
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

const ENDPOINTS = {
  SETTINGS: `${API_BASE_URL}/settings`,
  LOGIN: `${API_BASE_URL}/user/auth/login`,
  REQUEST_OTP: `${API_BASE_URL}/auth/request-otp`,
  VERIFY_OTP: `${API_BASE_URL}/auth/verify-otp`,
};

const FALLBACK_SETTINGS = {
  siteTitle: "EarnMedia",
  siteLogo: null,
  siteDescription: "Welcome back! Please enter your details.",
  favicon: null,
};

export default function Login() {
  // Brand & Settings State
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);

  // Password Login State
  const [emailOrNumber, setEmailOrNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ emailOrNumber: "", password: "", captcha: "" });
  const navigate = useNavigate();

  // OTP Modal State & Animated Lifecycle State
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isModalAnimating, setIsModalAnimating] = useState(false);
  const [otpStep, setOtpStep] = useState(1); // 1: Email Input, 2: 4-Digit Code
  const [otpEmail, setOtpEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpResendTimer, setOtpResendTimer] = useState(0);

  const recaptchaRef = useRef(null);
  const otpInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Helper to format payload dynamically for backend flexibility
  const getAuthPayload = (value) => {
    const trimmed = value.trim();
    const isPhone = /^\+?[0-9]{7,15}$/.test(trimmed);
    return {
      emailOrNumber: trimmed,
      ...(isPhone ? { phone: trimmed, mobile: trimmed } : { email: trimmed })
    };
  };

  // Helper to store session tokens, set auth cookie, & redirect
  const handleAuthSuccess = (data) => {
    const token =
      data.token ||
      data.accessToken ||
      data.jwt ||
      data.data?.token;

    const user =
      data.user ||
      data.userData ||
      data.data?.user;

    if (token) {
      document.cookie = `authToken=${encodeURIComponent(token)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
    }

    if (user) {
      document.cookie = `user=${encodeURIComponent(JSON.stringify(user))}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
    }

    toast.success(data.msg || "Login successful!");

    navigate("/", { replace: true });
  };
  // Fetch Branding Settings
  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      try {
        const response = await axios.get(ENDPOINTS.SETTINGS, { timeout: 5000 });
        if (isMounted && response.data) {
          const resData = response.data.data || response.data;
          const data = {
            siteTitle: resData.siteTitle || FALLBACK_SETTINGS.siteTitle,
            siteLogo: resData.siteLogo || FALLBACK_SETTINGS.siteLogo,
            siteDescription: resData.siteDescription || FALLBACK_SETTINGS.siteDescription,
            favicon: resData.favicon || FALLBACK_SETTINGS.favicon,
          };
          setSettings(data);
          updateBranding(data);
        }
      } catch (err) {
        if (isMounted) updateBranding(FALLBACK_SETTINGS);
      }
    };

    fetchSettings();
    return () => { isMounted = false; };
  }, []);

  // OTP Resend Countdown Timer
  useEffect(() => {
    let interval = null;
    if (otpResendTimer > 0) {
      interval = setInterval(() => {
        setOtpResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpResendTimer]);

  const updateBranding = (data) => {
    if (data.siteTitle) document.title = `${data.siteTitle} - Login`;
    if (data.favicon) {
      let link = document.querySelector("link[rel*='icon']") || document.createElement("link");
      link.rel = "shortcut icon";
      link.href = data.favicon;
      document.getElementsByTagName("head")[0].appendChild(link);
    }
  };

  // Client Validation for Password Login
  const validate = () => {
    const errors = { emailOrNumber: "", password: "", captcha: "" };
    let isValid = true;

    if (!emailOrNumber.trim()) {
      errors.emailOrNumber = "Email or mobile number is required.";
      isValid = false;
    }

    if (!password) {
      errors.password = "Password is required.";
      isValid = false;
    }

    if (!captchaToken && RECAPTCHA_SITE_KEY) {
      errors.captcha = "Please complete the CAPTCHA verification.";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  // Handle Standard Login Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;
    setLoading(true);

    try {
      const payload = {
        ...getAuthPayload(emailOrNumber),
        password,
        cfToken: captchaToken,
        ...(captchaToken && { captchaToken }),
      };

      const response = await axios.post(
        ENDPOINTS.LOGIN,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;
      // Checks backend response format: { status: true, token: "..." }
      if (data && (data.status === true || data.success || data.token)) {
        handleAuthSuccess(data);
      } else {
        toast.error(data.msg || "Invalid credentials");

        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
    } catch (err) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
      handleAxiosError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // Generic Error Handler
  const handleAxiosError = (err, setErrorState) => {
    if (err.response) {
      const status = err.response.status;
      const msg = err.response.data?.msg || err.response.data?.message || err.response.data?.error;
      switch (status) {
        case 400: setErrorState(msg || "Invalid request parameters."); break;
        case 401:
        case 404: setErrorState(msg || "Invalid account details or OTP."); break;
        case 403: setErrorState(msg || "Your account has been disabled."); break;
        case 422: setErrorState(msg || "Validation error. Please verify input."); break;
        case 429: setErrorState("Too many attempts. Please try again later."); break;
        default: setErrorState(msg || "Server error occurred. Please try again later.");
      }
    } else if (err.request) {
      setErrorState("Unable to connect to server. Check network connection.");
    } else {
      setErrorState(err.message || "An unexpected error occurred.");
    }
  };

  // ================= ANIMATED OTP MODAL HANDLERS =================

  const handleOpenOtpModal = () => {
    setIsOtpModalOpen(true);
    setOtpStep(1);
    setOtpEmail(emailOrNumber);
    setOtpDigits(["", "", "", ""]);
    setOtpError(null);
    requestAnimationFrame(() => {
      setIsModalAnimating(true);
    });
  };

  const handleCloseOtpModal = () => {
    setIsModalAnimating(false);
    setTimeout(() => {
      setIsOtpModalOpen(false);
      setOtpError(null);
    }, 300);
  };

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    setOtpError(null);

    if (!otpEmail.trim()) {
      setOtpError("Please enter your email or mobile number.");
      return;
    }

    setOtpLoading(true);
    try {
      const payload = getAuthPayload(otpEmail);
      await axios.post(ENDPOINTS.REQUEST_OTP, payload, {
        headers: { "Content-Type": "application/json" }
      });
      setOtpStep(2);
      setOtpResendTimer(60);
      setTimeout(() => otpInputRefs[0].current?.focus(), 150);
    } catch (err) {
      handleAxiosError(err, setOtpError);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);

    if (value && index < 3) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{4}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpDigits(digits);
      otpInputRefs[3].current?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    setOtpError(null);

    const code = otpDigits.join("");
    if (code.length < 4) {
      setOtpError("Please enter the full 4-digit code.");
      return;
    }

    setOtpLoading(true);
    try {
      const payload = {
        ...getAuthPayload(otpEmail),
        otp: code,
        code: code
      };

      const response = await axios.post(
        ENDPOINTS.VERIFY_OTP,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;
      if (data && (data.status === true || data.success || data.token)) {
        handleAuthSuccess(data);
      } else {
        throw new Error(data.msg || data.message || "Invalid OTP code.");
      }
    } catch (err) {
      handleAxiosError(err, setOtpError);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0f0f0f] flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden font-sans antialiased selection:bg-[#059669] selection:text-white transition-colors duration-500">

      {/* ================= BACKGROUND GRADIENT ACCENTS ================= */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] transition-opacity duration-500" />
        <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] bg-gradient-to-tr from-[#000000] via-[#059669]/20 to-[#10b981]/15 rounded-full blur-[100px] animate-[pulse_9s_infinite_ease-in-out] transition-all duration-700" />
        <div className="absolute -bottom-40 -right-40 w-[38rem] h-[38rem] bg-gradient-to-br from-[#059669]/15 via-[#000000] to-[#047857]/20 rounded-full blur-[120px] animate-[pulse_12s_infinite_ease-in-out] [animation-delay:3s] transition-all duration-700" />
      </div>

      {/* ================= MAIN CONTAINER CARD ================= */}
      <main className="w-full max-w-[420px] relative z-10 transition-all duration-500 ease-out transform translate-y-0 opacity-100">
        <div className="bg-[#141414]/90 backdrop-blur-xl rounded-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-7 sm:p-9 transition-all duration-300 ease-out hover:border-[#059669]/40 hover:shadow-[0_25px_60px_rgba(5,150,105,0.15)]">

          {/* BRANDING HEADER */}
          <div className="text-center mb-8 transition-all duration-300">
            <div className="inline-block group mb-3">
              {settings.siteLogo ? (
                <img
                  src={settings.siteLogo}
                  alt={settings.siteTitle}
                  className="h-10 w-auto mx-auto object-contain transition-all duration-300 ease-out group-hover:scale-105"
                />
              ) : (
                <div className="w-11 h-11 mx-auto rounded-lg bg-[#059669] text-white flex items-center justify-center font-extrabold text-xl shadow-[0_4px_14px_rgba(5,150,105,0.4)] transition-all duration-300 ease-out group-hover:scale-105 group-hover:bg-[#047857]">
                  {settings.siteTitle ? settings.siteTitle.charAt(0).toUpperCase() : "E"}
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-white tracking-tight mb-1.5 transition-colors duration-300">
              {settings.siteTitle}
            </h1>
            <p className="text-xs text-gray-400 leading-relaxed max-w-xs mx-auto font-normal transition-colors duration-300">
              {settings.siteDescription}
            </p>
          </div>

          {/* ERROR ALERT */}
          {error && (
            <div
              role="alert"
              className="mb-5 p-3 rounded-lg bg-rose-950/40 border border-rose-800/50 text-rose-300 text-xs flex items-start gap-2.5 transition-all duration-300 ease-out animate-[fadeIn_0.2s_ease-out]"
            >
              <svg className="w-4 h-4 text-rose-400 shrink-0 mt-0.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="transition-opacity duration-300">{error}</span>
            </div>
          )}

          {/* LOGIN FORM */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            {/* EMAIL / MOBILE FIELD */}
            <div>
              <div className="relative">
                <input
                  type="text"
                  id="emailOrNumber"
                  name="emailOrNumber"
                  value={emailOrNumber}
                  onChange={(e) => {
                    setEmailOrNumber(e.target.value);
                    if (fieldErrors.emailOrNumber) setFieldErrors(prev => ({ ...prev, emailOrNumber: "" }));
                  }}
                  placeholder=" "
                  aria-invalid={!!fieldErrors.emailOrNumber}
                  aria-describedby={fieldErrors.emailOrNumber ? "email-error" : undefined}
                  className={`peer w-full px-3.5 pt-5 pb-1.5 text-sm text-white bg-black/40 border ${fieldErrors.emailOrNumber ? "border-rose-500/80 bg-rose-950/10" : "border-white/10"
                    } rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/60 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20`}
                />
                <label
                  htmlFor="emailOrNumber"
                  className="absolute text-xs text-gray-400 duration-300 ease-out transform -translate-y-2 scale-90 top-3.5 z-10 origin-[0] left-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 peer-focus:scale-90 peer-focus:-translate-y-2 peer-focus:text-[#059669] pointer-events-none transition-all"
                >
                  Email or Mobile Number
                </label>
              </div>
              {fieldErrors.emailOrNumber && (
                <p id="email-error" className="mt-1 ml-1 text-xs text-rose-400 transition-all duration-200 ease-out">
                  {fieldErrors.emailOrNumber}
                </p>
              )}
            </div>

            {/* PASSWORD FIELD */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: "" }));
                  }}
                  placeholder=" "
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "password-error" : undefined}
                  className={`peer w-full pl-3.5 pr-11 pt-5 pb-1.5 text-sm text-white bg-black/40 border ${fieldErrors.password ? "border-rose-500/80 bg-rose-950/10" : "border-white/10"
                    } rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/60 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20`}
                />
                <label
                  htmlFor="password"
                  className="absolute text-xs text-gray-400 duration-300 ease-out transform -translate-y-2 scale-90 top-3.5 z-10 origin-[0] left-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 peer-focus:scale-90 peer-focus:-translate-y-2 peer-focus:text-[#059669] pointer-events-none transition-all"
                >
                  Password
                </label>

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white focus:outline-none transition-colors duration-200"
                >
                  {showPassword ? (
                    <svg className="w-4 h-4 transition-transform duration-200 active:scale-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M13.875 18.825A10.05 10.05 0 0112 19c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 transition-transform duration-200 active:scale-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p id="password-error" className="mt-1 ml-1 text-xs text-rose-400 transition-all duration-200 ease-out">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* RECAPTCHA CONTAINER */}
            {RECAPTCHA_SITE_KEY ? (
              <div className="pt-1 transition-all duration-300">
                <div className="flex justify-center overflow-hidden rounded-lg border border-white/10 p-1 bg-black/30 transition-all duration-300 hover:border-white/20">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => {
                      setCaptchaToken(token);
                      if (fieldErrors.captcha) setFieldErrors(prev => ({ ...prev, captcha: "" }));
                    }}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>
                {fieldErrors.captcha && (
                  <p className="mt-1 text-center text-xs text-rose-400 transition-all duration-200 ease-out">
                    {fieldErrors.captcha}
                  </p>
                )}
              </div>
            ) : null}

            {/* SIGN IN BUTTON */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#059669] hover:bg-[#047857] text-white font-medium py-3 px-4 rounded-lg shadow-[0_4px_16px_rgba(5,150,105,0.3)] hover:shadow-[0_6px_20px_rgba(5,150,105,0.4)] active:scale-[0.99] transition-all duration-300 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="flex items-center justify-center gap-2 text-sm tracking-wide transition-all duration-300">
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white transition-opacity duration-300" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </span>
            </button>

          </form>

          {/* SEPARATOR */}
          <div className="relative my-5 text-center transition-all duration-300">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-white/10 transition-colors duration-300" />
            </div>
            <div className="relative inline-block bg-[#141414] px-3 text-xs font-medium text-gray-500 tracking-wider transition-colors duration-300">
              OR
            </div>
          </div>

          {/* LOGIN WITH OTP BUTTON (OPENS MODAL) */}
          <button
            type="button"
            onClick={handleOpenOtpModal}
            className="w-full bg-black/40 hover:bg-black/80 text-gray-200 hover:text-white font-medium py-2.5 px-4 border border-white/10 hover:border-[#059669]/60 rounded-lg shadow-sm active:scale-[0.99] transition-all duration-300 ease-out flex items-center justify-center gap-2 text-sm group"
          >
            <svg className="w-4 h-4 text-[#059669] transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="transition-colors duration-300">Login with OTP</span>
          </button>

          {/* DONT HAVE AN ACCOUNT REDIRECT */}
          <div className="mt-6 text-center text-xs text-gray-400">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/register")}
              className="font-medium text-[#059669] hover:text-[#047857] hover:underline transition-colors duration-200 ease-out"
            >
              Register
            </button>
          </div>

        </div>
      </main>

      {/* ================= ANIMATED OTP MODAL ================= */}
      {isOtpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">

          {/* Backdrop Blur Overlay with Smooth Fade */}
          <div
            onClick={handleCloseOtpModal}
            className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ease-out ${isModalAnimating ? "opacity-100" : "opacity-0"
              }`}
          />

          {/* Modal Content Box with Smooth Scale + Fade */}
          <div
            className={`relative w-full max-w-sm bg-[#141414] rounded-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.8)] p-6 sm:p-7 z-10 overflow-hidden transform transition-all duration-300 ease-out ${isModalAnimating ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-2"
              }`}
          >

            {/* Modal Close Icon */}
            <button
              type="button"
              onClick={handleCloseOtpModal}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-200 ease-out active:scale-90"
            >
              <svg className="w-4 h-4 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Header */}
            <div className="text-center mb-5 transition-all duration-300">
              <div className="w-10 h-10 mx-auto mb-2.5 rounded-lg bg-[#059669]/20 border border-[#059669]/40 text-[#059669] flex items-center justify-center transition-all duration-300">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-white transition-colors duration-300">
                {otpStep === 1 ? "OTP Verification" : "Enter Verification Code"}
              </h2>
              <p className="text-xs text-gray-400 mt-1 transition-colors duration-300">
                {otpStep === 1
                  ? "We will send a 4-digit security code to your email/mobile."
                  : `Code sent to ${otpEmail}`}
              </p>
            </div>

            {/* Modal Error Alert */}
            {otpError && (
              <div className="mb-4 p-2.5 rounded-lg bg-rose-950/40 border border-rose-800/50 text-rose-300 text-xs flex items-center gap-2 transition-all duration-300 ease-out">
                <svg className="w-4 h-4 text-rose-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{otpError}</span>
              </div>
            )}

            {/* STEP 1: EMAIL INPUT */}
            {otpStep === 1 ? (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    id="otpEmail"
                    value={otpEmail}
                    onChange={(e) => setOtpEmail(e.target.value)}
                    placeholder=" "
                    className="peer w-full px-3.5 pt-5 pb-1.5 text-sm text-white bg-black/40 border border-white/10 rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/60 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/20"
                  />
                  <label
                    htmlFor="otpEmail"
                    className="absolute text-xs text-gray-400 duration-300 ease-out transform -translate-y-2 scale-90 top-3.5 z-10 origin-[0] left-3.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-xs peer-placeholder-shown:text-gray-400 peer-focus:scale-90 peer-focus:-translate-y-2 peer-focus:text-[#059669] pointer-events-none transition-all"
                  >
                    Email or Mobile Number
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full bg-[#059669] hover:bg-[#047857] text-white font-medium py-2.5 px-4 rounded-lg shadow-[0_4px_14px_rgba(5,150,105,0.3)] active:scale-[0.99] transition-all duration-300 ease-out flex items-center justify-center text-sm disabled:opacity-60"
                >
                  {otpLoading ? "Sending Code..." : "Send Verification Code"}
                </button>
              </form>
            ) : (
              /* STEP 2: 4-DIGIT CODE INPUT */
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="flex justify-center items-center gap-2.5">
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={otpInputRefs[idx]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      onPaste={handleOtpPaste}
                      className="w-11 h-12 text-center text-lg font-bold text-white bg-black/50 border border-white/10 rounded-lg outline-none transition-all duration-300 ease-out focus:bg-black/70 focus:border-[#059669] focus:ring-2 focus:ring-[#059669]/30"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full bg-[#059669] hover:bg-[#047857] text-white font-medium py-2.5 px-4 rounded-lg shadow-[0_4px_14px_rgba(5,150,105,0.3)] active:scale-[0.99] transition-all duration-300 ease-out flex items-center justify-center text-sm disabled:opacity-60"
                >
                  {otpLoading ? "Verifying..." : "Verify & Sign In"}
                </button>

                {/* Resend & Back Controls */}
                <div className="flex items-center justify-between text-xs text-gray-400 pt-1">
                  <button
                    type="button"
                    onClick={() => setOtpStep(1)}
                    className="hover:text-[#059669] hover:underline transition-colors duration-200 ease-out"
                  >
                    Change detail
                  </button>

                  <button
                    type="button"
                    disabled={otpResendTimer > 0 || otpLoading}
                    onClick={handleRequestOtp}
                    className="font-medium text-[#059669] hover:text-[#047857] disabled:text-gray-500 disabled:no-underline transition-colors duration-200 ease-out"
                  >
                    {otpResendTimer > 0 ? `Resend code in ${otpResendTimer}s` : "Resend Code"}
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}

    </div>
  );
}